import { capitalize } from 'lodash';
import Image from 'next/image';

import { Badge, Card, CardContent, Text } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import { SelectTriggerOption } from '../TriggerTypeSelectorModal';

interface Props {
  option: SelectTriggerOption;
  selectedCount: number;
  onClick: () => void;
}

export const TriggerTypeSelectorCard = ({ option, selectedCount, onClick }: Props) => {
  return (
    <Card
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={option.title}
      aria-pressed={selectedCount > 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'tw:w-full tw:h-full tw:cursor-pointer tw:hover:bg-indigo-500/[0.08] tw:active:bg-indigo-500/[0.12]',
        'tw:focus-visible:ring-2 tw:focus-visible:ring-offset-2 tw:focus-visible:ring-blue-500 tw:outline-none',
        selectedCount > 0 && 'tw:border-2 tw:border-blue-500',
      )}
    >
      <CardContent className="tw:h-full">
        <div className="tw:flex tw:flex-row tw:items-start tw:gap-4">
          {typeof option.imageSrc === 'string' ? (
            <Image
              alt={option.title}
              width={40}
              height={40}
              src={option.imageSrc}
              style={{ flexShrink: 0, width: 40, height: 40, objectFit: 'contain' }}
            />
          ) : (
            <div className="tw:w-10 tw:h-10 tw:min-w-[40px] tw:min-h-[40px] tw:flex tw:justify-center tw:items-center tw:shrink-0">
              {option.imageSrc}
            </div>
          )}
          <div className="tw:flex tw:flex-col tw:gap-3 tw:flex-1">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <div className="tw:flex tw:flex-row tw:justify-between tw:items-start tw:gap-2">
                <Text variant="body-sm" weight="bold">
                  {option.title}
                </Text>
                {selectedCount > 0 && (
                  <Badge size="sm" className="tw:shrink-0">
                    {selectedCount}
                  </Badge>
                )}
              </div>
              <Text variant="body-sm" color="muted">
                {option.description}
              </Text>
            </div>
            <Badge size="sm" variant="outline" className="tw:self-start">
              {capitalize(option.category)}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
